import { useState, useEffect } from 'react';

interface PlaceDetails {
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  formattedAddress: string;
}

export const useGooglePlaceDetails = (placeId: string | undefined) => {
  const [placeDetails, setPlaceDetails] = useState<PlaceDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!placeId || !window.google?.maps) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const service = new google.maps.places.PlacesService(
      document.createElement('div')
    );

    service.getDetails(
      {
        placeId,
        fields: ['address_components', 'formatted_address'],
      },
      (place, status) => {
        setIsLoading(false);

        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          const details: PlaceDetails = {
            address: '',
            city: '',
            state: '',
            country: '',
            postalCode: '',
            formattedAddress: place.formatted_address || '',
          };

          place.address_components?.forEach(component => {
            const types = component.types;

            if (types.includes('street_number')) {
              details.address = component.long_name + ' ';
            }
            if (types.includes('route')) {
              details.address += component.long_name;
            }
            if (types.includes('locality')) {
              details.city = component.long_name;
            }
            if (types.includes('administrative_area_level_1')) {
              details.state = component.short_name;
            }
            if (types.includes('country')) {
              details.country = component.long_name;
            }
            if (types.includes('postal_code')) {
              details.postalCode = component.long_name;
            }
          });

          setPlaceDetails(details);
        } else {
          setError('Failed to fetch place details');
        }
      }
    );
  }, [placeId]);

  return { placeDetails, isLoading, error };
};
